import React, { useCallback, useEffect, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import _ from 'lodash';
import { Loading } from '../loading/Loading';
import styles from './index.module.scss';

const theme = {
  container: {
  },
  input: {
    width: '100%',
    padding: '0px 15px',
    border: '0px',
    backgroundColor: '#F3F3F3',
    borderRadius: '0px',
    color: '#161616',
    fontSize: '14px',
    lineHeight: '18px',
    height: '35px',
    zIndex: '999 !important',
  },
  inputFocused: {
    backgroundColor: '#fff',
  },
  inputOpen: {

  },
  suggestionsContainer: {
    position: 'absolute',
    width: '80%',
    overflow: 'auto',
    maxHeight: '250px',
    backgroundColor: '#FEFEFE',
    boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.1)',
    borderRadius: '0px 0px 8px 8px',
    zIndex: 1000,
  },
  suggestionsContainerOpen: {
    paddingTop: '0%',
    paddingBottom: '0%',
    border: '1px solid #EDEDED',
  },
  suggestionsList: {
    paddingLeft: '0%',
    marginBottom: '0px',
  },
  suggestion: {
    display: 'flex',
    flexDirection: 'row',
    listStyle: 'none',
    padding: '2% 3% 0%',
  },
  suggestionHighlighted: {
    backgroundColor: '#F7F7F7',
    cursor: 'pointer',
    transition: 'ease-in-out 0.12s',
  },
};

const suggestionEmailStyle = {
  fontSize: '12px',
  color: '#000',
  opacity: '0.6',
};

const imageStyle = {
  height: '40px',
  width: '40px',
  marginRight: '10px',
  borderRadius: '5px',
};

const suggestionDetailStyle = {
  display: 'flex',
  flexDirection: 'column' as 'column',
  width: '90%',
  paddingBottom: '10px',
  borderBottom: '2px solid #F5F7F9',
};

const suggestionLoadingStyle = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  padding: '15px',
};

const noSuggestionStyle = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  padding: '15px',
  fontSize: '16px',
  color: '#525252',
};

const SearchBar = props => {
  const [suggestions, setSuggestions] = useState<Record<string, any>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [noSuggestionsFound, setNoSuggestionsFound] = useState<boolean>(false);
  const [renderSuggestions, setRenderSuggestions] = useState<boolean>(true);
  const {
    isUser,
    value,
    local,
    setValue,
    onSuggestionClicked,
    searchAPI,
    searchAPIParam,
    searchAPIParamKey,
    defaultSuggestions,
    clearOnSelect,
    disabled,
    suggestionTitleAccessor = 'displayName',
  } = props;

  useEffect(() => {
    if (local && !!defaultSuggestions.length) setRenderSuggestions(true);
  }, [value]);
  
  const onChange = async (e, { newValue }) => {
    setRenderSuggestions(true);
    setValue(newValue);
  };

  const onSuggestionsFetchRequested = async ({ value }) => {
    if (value.length >= 2) {
      if (!local) {
        setLoading(true);
        setSuggestions([{}]);
        const result = await searchAPI({ [searchAPIParamKey]: value, ...searchAPIParam });
        if (!result.length) {
          setNoSuggestionsFound(true);
          setSuggestions([{}]);
        } else {
          setNoSuggestionsFound(false);
          setSuggestions(result);
        }
        setLoading(false);
      } else {
        setSuggestions(defaultSuggestions.filter(suggestion => {
          return suggestion.displayName.toLowerCase().includes(value.toLowerCase());
        }));
      }
    }
    if (!value.length) {
      setSuggestions(defaultSuggestions);
    }
  };

  const onSuggestionsFetchRequestedDebounced = useCallback(_.debounce(onSuggestionsFetchRequested, 500), [defaultSuggestions]);

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const getSuggestionValue = suggestion => {
    if (!isUser) {
      return suggestion.displayName;
    } else {
      return suggestion.full_name;
    }
  };

  const getSuggestionImage = url => {
    return url || '';
  };

  const renderSuggestion = suggestion => {
    if (loading) {
      return (
        <div style={suggestionLoadingStyle}>
          <Loading type={'mini'} />
        </div>
      );
    }

    if (noSuggestionsFound) {
      return <div style={noSuggestionStyle}>No results found</div>;
    }

    if (isUser) {
      return (
        <div className='suggestion-details' style={suggestionDetailStyle}>
          <div className='product-name'>{suggestion.full_name}</div>
          {suggestion.email ? (
            <div style={suggestionEmailStyle}>{suggestion.email}</div>
          ) : null}
        </div>
      );
    }

    return (
      <>
        {suggestion.image && (
          <img src={getSuggestionImage(suggestion.image)} style={imageStyle} />
        )}
        <div className='suggestion-details' style={suggestionDetailStyle}>
          <span className={styles['product-name']}>{suggestion[suggestionTitleAccessor]}</span>
          <span className={styles['product-category']}>{suggestion.category?.displayName}</span>
        </div>
      </>
    );
  };

  const shouldRenderSuggestions = () => renderSuggestions;
  
  const getSuggestionDisplayValue = (suggestion: Record<string, any>) => {
    if (clearOnSelect) return '';
    return suggestion.displayName || suggestion.full_name || '';
  };
  
  const onSuggestionSelected = (e, { suggestion }) => {
    setRenderSuggestions(false);
    setValue(getSuggestionDisplayValue(suggestion));
    onSuggestionClicked(suggestion);
  };
  
  const inputProps = {
    placeholder: 'Search',
    value,
    onChange,
    disabled,
  };

  const getSuggestions = () => {
    if (suggestions?.length) return suggestions;
    else if (!value && defaultSuggestions?.length) return defaultSuggestions;
    return [{ }];
  };
  
  return (
    <>
      <Autosuggest
        theme={theme}
        suggestions={getSuggestions()}
        onSuggestionsFetchRequested={onSuggestionsFetchRequestedDebounced}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        shouldRenderSuggestions={shouldRenderSuggestions}
        inputProps={inputProps}
      />
    </>
  );
};

export default SearchBar;
